import { useEffect, useRef, useState } from 'react'
import { useClipQueue } from '../lib/clipQueue'
import { MAX_SECONDS, MIN_SECONDS, mediaSeconds } from '../lib/videoClip'
import { mb as size } from '../lib/compress'
import TrimStrip from './TrimStrip'

/**
 * The Collective clip — a short vertical video that is NOT the song.
 *
 * Deliberately not part of MediaUpload. The recording a church listens to must
 * never be re-encoded or shortened; a clip is always both. Same writer, same
 * song, opposite rules, so they are separate components on purpose.
 *
 * This component picks the file and takes the trim. The actual work is handed
 * to the clip queue, which lives above the router — so the writer can walk
 * away the moment they confirm and the encode carries on without them
 * watching it.
 *
 * The trim is not optional dressing on this path either. A cover is filmed in
 * one take with a false start at the front of it more often than not, and
 * before the strip existed the encoder just kept the first sixty seconds of
 * whatever arrived.
 */
export default function ClipUpload({ song, onChange, kind = 'original', clip = null }) {
  const input = useRef(null)
  const { job, start } = useClipQueue()
  const isCover = kind === 'cover'

  // The file the writer picked, held here until they've said which part of it
  // they meant. Nothing is encoded or uploaded while this is set.
  const [pick, setPick] = useState(null)   // { file, url, seconds }
  const [win, setWin] = useState({ start: 0, end: MAX_SECONDS })
  const [err, setErr] = useState(null)

  useEffect(() => () => { if (pick?.url) URL.revokeObjectURL(pick.url) }, [pick])

  const choose = async (file) => {
    setErr(null)
    const secs = await mediaSeconds(file, 'video')
    if (!Number.isFinite(secs)) return setErr("That video couldn't be read.")
    if (secs < MIN_SECONDS) {
      return setErr(`That's ${Math.round(secs)} seconds. A clip needs at least `
        + `${MIN_SECONDS} — long enough for someone to hear the idea.`)
    }
    setWin({ start: 0, end: Math.min(secs, MAX_SECONDS) })
    setPick({ file, url: URL.createObjectURL(file), seconds: secs })
  }

  const send = () => {
    start(pick.file, song, onChange, kind, null, win)
    setPick(null)
  }

  const mine = job?.songId === song.id
  const working = mine && (job.phase === 'preparing' || job.phase === 'posting')
  const busyElsewhere = job && !mine
    && (job.phase === 'preparing' || job.phase === 'posting')

  return (
    <>
      <label className="label">
        {isCover ? 'Your cover' : 'A hook for the Collective'}
      </label>
      <p className="small" style={{ marginTop: -4, marginBottom: 10 }}>
        {isCover
          ? `Up to ${MAX_SECONDS} seconds of your version. It goes to the
             Collective credited to you, with the original writer named.`
          : `Up to ${MAX_SECONDS} seconds of the bit worth hearing. This is what
             other writers scroll past — it doesn't change what you send to a
             church.`}
      </p>

      {clip && !working && (
        <p className="small" style={{ marginBottom: 10 }}>
          {isCover ? 'Your cover is up' : "It's up"}
          {clip.duration_seconds ? ` — ${clip.duration_seconds}s` : ''}.
          Choosing another replaces it.
        </p>
      )}

      {mine && job.phase === 'done' && job.savedTo && (
        <p className="small" style={{ marginBottom: 10, color: 'var(--ok)' }}>
          Posted — {size(job.savedFrom)} → {size(job.savedTo)}.
        </p>
      )}

      <input
        ref={input}
        type="file"
        hidden
        accept="video/*,.mov,.mp4,.m4v,.webm"
        onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) choose(f)
        }}
      />

      {pick && (
        <>
          <TrimStrip url={pick.url} seconds={pick.seconds}
                     value={win} onChange={setWin} />
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button type="button" className="btn btn-primary" style={{ flex: 1 }}
                    onClick={send}>Use this bit</button>
            <button type="button" className="btn btn-quiet" style={{ flex: 1 }}
                    onClick={() => setPick(null)}>Cancel</button>
          </div>
        </>
      )}

      {err && <p className="err" style={{ marginTop: 10 }}>{err}</p>}

      {!pick && (
      <button type="button" className="btn btn-quiet"
              disabled={working || busyElsewhere}
              onClick={() => input.current?.click()}>
        {working ? 'Working on it…'
          : busyElsewhere ? 'Another clip is being prepared'
          : clip ? (isCover ? 'Replace your cover' : 'Replace the hook')
          : (isCover ? 'Record a cover' : 'Add a hook')}
      </button>
      )}

      {working && (
        <p className="small" style={{ marginTop: 8 }}>
          Carry on — it keeps going while you use the app, and the bar at the
          bottom will tell you when it's up.
        </p>
      )}
    </>
  )
}
